/**
 * Sudoku Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * stats including best times and streaks.
 *
 * Follows ReversiStatisticsService pattern.
 */

import {
  SudokuStatistics,
  SudokuGameResult,
  SudokuDifficultyStats,
  SudokuDifficulty,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class SudokuStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'sudoku_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): SudokuStatistics {
    return {
      easy: this.createEmptyDifficultyStats(),
      medium: this.createEmptyDifficultyStats(),
      hard: this.createEmptyDifficultyStats(),
      totalGames: 0,
      totalPlayTime: 0,
      currentStreak: 0,
      bestStreak: 0,
    };
  }

  private createEmptyDifficultyStats(): SudokuDifficultyStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      bestTime: null,
      averageTime: 0,
      totalHintsUsed: 0,
      winPercentage: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): SudokuStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as SudokuStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get sudoku statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(result: SudokuGameResult, userId?: string): boolean {
    try {
      const stats = this.getStatistics(userId);
      const diffStats = stats[result.difficulty];

      diffStats.gamesPlayed++;

      if (result.completed) {
        diffStats.gamesWon++;

        // Update best time
        if (diffStats.bestTime === null || result.time < diffStats.bestTime) {
          diffStats.bestTime = result.time;
        }

        // Update average time
        const totalTime = diffStats.averageTime * (diffStats.gamesWon - 1) + result.time;
        diffStats.averageTime = Math.round(totalTime / diffStats.gamesWon);

        // Update streak
        stats.currentStreak++;
        if (stats.currentStreak > stats.bestStreak) {
          stats.bestStreak = stats.currentStreak;
        }
      } else {
        stats.currentStreak = 0;
      }

      diffStats.totalHintsUsed += result.hintsUsed;
      diffStats.winPercentage =
        diffStats.gamesPlayed > 0
          ? (diffStats.gamesWon / diffStats.gamesPlayed) * 100
          : 0;

      stats.totalGames++;
      stats.totalPlayTime += result.time;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record sudoku game result:', error);
      return false;
    }
  }

  /** Get statistics for a specific difficulty. */
  getDifficultyStatistics(
    difficulty: SudokuDifficulty,
    userId?: string
  ): SudokuDifficultyStats {
    const stats = this.getStatistics(userId);
    return stats[difficulty];
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset sudoku statistics:', error);
      return false;
    }
  }
}
